import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Save, Search } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { LoadingState } from "../../components/Ui";
import { getAdminLanding, updateAdminLanding } from "../../lib/api";
import type { LandingContent } from "../../types/content";
import { AdminHeader, Field, Notice } from "./AdminUi";

export function SettingsEditor() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ["admin-landing"], queryFn: getAdminLanding });
  const [content, setContent] = useState<LandingContent | null>(null);
  const [notice, setNotice] = useState("");
  useEffect(() => { if (query.data) setContent(query.data); }, [query.data]);
  const mutation = useMutation({
    mutationFn: () => updateAdminLanding(content!),
    onSuccess: (data) => {
      setContent(data);
      setNotice("Settings and SEO metadata saved.");
      queryClient.invalidateQueries({ queryKey: ["public-landing"] });
    },
  });
  if (!content) return <LoadingState label="Loading settings" />;

  function submit(event: FormEvent) {
    event.preventDefault();
    mutation.mutate();
  }

  return (
    <form onSubmit={submit}>
      <AdminHeader
        title="Settings & SEO"
        description="Search metadata and public company details."
        action={<button className="button button-primary" type="submit">Save settings <Save size={16} /></button>}
      />
      {notice && <Notice type="success" message={notice} onClose={() => setNotice("")} />}
      <div className="settings-grid">
        <section className="admin-card editor-card">
          <div className="card-heading"><div><h2>Search appearance</h2><p>Default page title and description</p></div><Search size={20} /></div>
          <div className="form-grid">
            <Field label="SEO title" hint={`${content.seo.title.length}/70 characters`}>
              <input maxLength={70} value={content.seo.title} onChange={(event) => setContent({ ...content, seo: { ...content.seo, title: event.target.value } })} />
            </Field>
            <Field label="Meta description" hint={`${content.seo.description.length}/160 characters`}>
              <textarea rows={5} maxLength={160} value={content.seo.description} onChange={(event) => setContent({ ...content, seo: { ...content.seo, description: event.target.value } })} />
            </Field>
            <Field label="Open Graph image URL">
              <input type="url" value={content.seo.ogImageUrl} onChange={(event) => setContent({ ...content, seo: { ...content.seo, ogImageUrl: event.target.value } })} />
            </Field>
          </div>
          <div className="search-preview">
            <small>steria.house</small>
            <strong>{content.seo.title}</strong>
            <p>{content.seo.description}</p>
          </div>
        </section>
        <section className="admin-card editor-card">
          <div className="card-heading"><div><h2>Company details</h2><p>Footer and contact information</p></div></div>
          <div className="form-grid">
            <Field label="Contact email">
              <input type="email" value={content.footer.email} onChange={(event) => setContent({ ...content, footer: { ...content.footer, email: event.target.value } })} />
            </Field>
            <Field label="Footer description">
              <textarea rows={5} value={content.footer.description} onChange={(event) => setContent({ ...content, footer: { ...content.footer, description: event.target.value } })} />
            </Field>
            <Field label="Copyright line">
              <input value={content.footer.copyright} onChange={(event) => setContent({ ...content, footer: { ...content.footer, copyright: event.target.value } })} />
            </Field>
          </div>
        </section>
      </div>
    </form>
  );
}
